"use client";

import { Button, Stack, TextField, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { useRequestContactFormMutation } from "modules/shared/api/hooks/useRequestContactFormMutation";
import Link from "next/link";
/* eslint-disable @next/next/no-sync-scripts */

import { useState } from "react";
import { Controller, useForm } from "react-hook-form";

const FIELDS = [
  {
    description: "",
    label: "Project name",
    name: "projectName",
  },
  {
    description: "Briefly describe what your project is about",
    label: "Project Description",
    name: "projectDescription",
  },
  {
    description: `Provide the link to your project's website.
        Add any other relevant links(e.g., GitHub, social media, demo, etc.) if available`,
    label: "Project Links",
    name: "projectLinks",
  },
  {
    description:
      "At what stage is your project currently? (e.g., Idea, Development, Beta, Live)",
    label: "Current Stage of the Project",
    name: "projectStage",
  },
  {
    description:
      "Is your project already developed or deployed on another blockchain? If yes please specify",
    label: "Development on Other Chains",
    name: "otherChains",
  },
  {
    description:
      "Who will be collaborating on this project? Please provide bios or links to your team profiles (e.g., Linkedin, Twitter)",
    label: "Team Information",
    name: "teamInformation",
  },
  {
    description: `What major problem are you aiming to solve?
        How will your project contribute the broader Prom ecosystem?
        Who will benefit from your project?`,
    label: "Project Impact",
    name: "projectImpact",
  },
  {
    description: `What are the most significant challenges your project is facing?
        What are your most critical needs?`,
    label: `Needs and Challenges`,
    name: "needsAndChallenges",
  },
  {
    description: `How much funding are you requesting?
        Provide a detailed breakdown of how the grant funds will be used(e.g., development, marketing, hiring, etc.).\
        Explain why this specific amount is necessary for your project's success`,
    label: `Grant Amount Needed`,
    name: "grantAmountNeeded",
  },
  {
    description:
      "Please provide any available project whitepaper or technical documentation that outlines the project's architecture, goals, and implementation plans: (Provide a link)",
    label: `Project documentation`,
    name: "projectDocumentation",
  },
  {
    description:
      "Please provide a link for detailed document that describes the tokenomics of your project, including aspects such as token supply, distribution mechanisms, utility, and economic model:",
    label: `Tokenomics Overview`,
    name: "tokenomicsOverview",
  },
  {
    description: "",
    label: `Contact name`,
    name: "contactName",
  },
  {
    description: "",
    label: `Contact email`,
    name: "contactEmail",
  },
];

const StyledBackground = styled(Stack)`
  min-height: calc(100vh - 515px);
  background: url("/background.png");
  background-size: cover;
  align-items: center;
  justify-content: center;
  padding: 40px;
`;

export const Grant = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const {
    control,
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm();

  const { mutate, isSuccess, isError } = useRequestContactFormMutation();
  const fields = watch();

  return (
    <StyledBackground>
      {isSuccess || isError ? (
        <Stack gap={20}>
          <Stack gap={4}>
            <Typography variant="h3">Thanks reaching out to us!</Typography>
            <Typography
              fontWeight={600}
              variant="subtitle1"
              color="text.secondary"
            >
              {/* eslint-disable-next-line react/no-unescaped-entities */}
              {isError ? (
                <>
                  Something went wrong, please try again later or contact
                  <a href="mailto:prom.form@prometeus.io">info@prometeus.io</a>
                  directly
                </>
              ) : (
                "We'll be in touch soon"
              )}
            </Typography>
          </Stack>
          <Link passHref href="/">
            <Button variant="contained" color="primary">
              Go to main page
            </Button>
          </Link>
        </Stack>
      ) : (
        <Stack maxWidth="600px" width="100%" gap={12}>
          <Typography fontSize="36px" fontWeight={600} textAlign="center">
            PROM Grant Program Application Form
          </Typography>

          <form
            onSubmit={handleSubmit((values) => {
              mutate(values);
            })}
          >
            <Stack
              sx={{
                background: "white",
                borderRadius: "20px",
                padding: 8,
                gap: "70px",
              }}
            >
              {FIELDS.map(({ label, description, name }, index) => (
                <Controller
                  control={control}
                  key={name}
                  render={({ field }) => {
                    return (
                      <TextField
                        multiline
                        placeholder="Type your answer here..."
                        InputLabelProps={{
                          sx: {
                            color: "black",
                            fontWeight: 600,
                            fontSize: "16px",
                            "& .MuiInputLabel-asterisk": {
                              color: "red",
                            },
                          },
                        }}
                        fullWidth
                        required
                        sx={{
                          display: index === currentStep ? "initial" : "none",
                        }}
                        key={name}
                        label={label}
                        helperText={description}
                        {...field}
                      />
                    );
                  }}
                  name={name}
                />
              ))}
              <Stack gap={5}>
                {currentStep === FIELDS.length - 1 ? (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!fields[FIELDS[currentStep].name] || isSubmitting}
                  >
                    Submit
                  </Button>
                ) : (
                  <Button
                    disabled={!fields[FIELDS[currentStep].name]}
                    variant="contained"
                    onClick={() => {
                      setCurrentStep((prev) => prev + 1);
                    }}
                  >
                    {" "}
                    Next {">"}{" "}
                  </Button>
                )}
                {currentStep > 0 ? (
                  <Button
                    onClick={() => setCurrentStep((prevState) => prevState - 1)}
                    variant="text"
                  >
                    Back
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </form>
        </Stack>
      )}
    </StyledBackground>
  );
};
